import { AxiosRequestConfig } from "axios";
import { pigspinBackofficeApiInstance } from "../../utils/api/AxiosInstance";
import { fetchWithJSON } from "../../utils/api/fetch";
import { BaseRequest, BaseResponse2 } from "../service.type";

// The three real storefronts a website-system row can target. Distinct from the
// legacy src/services/systemStatus ProductType, which also allows "ALL" as a
// product value for its own (different) per-product-tab query - this group API
// never accepts "ALL" inside products[]; ALL is only ever the row's top-level
// `scope`.
export type WebsiteSystemProduct = "PIG_SPIN" | "PIG_BET" | "PIG_SPORT";

// Fixed display order for the three sites (PIGSLOT, PIGBET, PIGSPORT) - the
// table's "เว็บที่แสดง" column, the edit modal's site picker and the PUT's
// products[] all follow it.
export const WEBSITE_SYSTEM_PRODUCTS: WebsiteSystemProduct[] = ["PIG_SPIN", "PIG_BET", "PIG_SPORT"];

export type WebsiteSystemScope = "PER_PRODUCT" | "ALL";

// `selected` is the admin's saved choice of sites for this row and survives the
// switch being turned off (or the schedule sweep turning it off); `enabled` is
// whether that site is showing the announcement right now. For an ALL-scope row
// (Tip/Rain) every product is always selected.
export type WebsiteSystemProductEnabled = {
  product: WebsiteSystemProduct;
  enabled: boolean;
  selected: boolean;
};

// GET .../website-system response item - one row per code (GameError, KYCError,
// Tip, Rain, always in that order).
export type WebsiteSystemFeatureItem = {
  code: string;
  name: string;
  scope: WebsiteSystemScope;
  enabled: boolean;
  products: WebsiteSystemProductEnabled[];
  type_time: "now" | "schedule" | "custom";
  days_week: string | null;
  start_time: string | null;
  end_time: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  title: string | null;
  message: string | null;
  link_url: string | null;
  link_label: string | null;
  toast: boolean;
  notification: boolean;
  notification_type: string;
  updated_at: string | null;
  updated_by: string | null;
};

export type GetWebsiteSystemRequest = BaseRequest;

export type GetWebsiteSystemResponse = BaseResponse2<{
  feature_list: WebsiteSystemFeatureItem[];
}>;

export const GetWebsiteSystem = async (
  request: GetWebsiteSystemRequest
): Promise<GetWebsiteSystemResponse> => {
  const requestConfig: AxiosRequestConfig = {
    url: "v1/feature-toggle/website-system",
    method: "GET",
    headers: { token: request.token },
  };
  return fetchWithJSON<GetWebsiteSystemResponse>(pigspinBackofficeApiInstance, requestConfig);
};

// PATCH .../website-system/{code}/toggle - one switch per row. The body is
// `{enabled}` only: turning a PER_PRODUCT row on shows it on the row's selected
// sites, turning it off hides it everywhere. The backend 400s on a `product` key
// in this body, so it must never be sent.
export type ToggleWebsiteSystemRequest = BaseRequest & {
  code: string;
  enabled: boolean;
};

export type ToggleWebsiteSystemResponse = BaseResponse2<{}>;

export const ToggleWebsiteSystem = async (
  request: ToggleWebsiteSystemRequest
): Promise<ToggleWebsiteSystemResponse> => {
  const { token, code, enabled } = request;
  const requestConfig: AxiosRequestConfig = {
    url: `v1/feature-toggle/website-system/${code}/toggle`,
    method: "PATCH",
    headers: { token },
    data: { enabled },
  };
  return fetchWithJSON<ToggleWebsiteSystemResponse>(pigspinBackofficeApiInstance, requestConfig);
};

// PUT .../website-system/{code} - `products` is the list of selected sites,
// sent for a PER_PRODUCT code only (at least one, else the backend 400s) and
// omitted for an ALL-scope code. If the row's switch is on, the new list takes
// effect right away; if it is off, the list is only remembered for the next
// time it is switched on.
// The backend requires `message`, `toast` and `notification`; title/link_url/
// link_label are left out, which it stores as null.
export type UpdateWebsiteSystemRequest = BaseRequest & {
  code: string;
  message: string;
  toast: boolean;
  notification: boolean;
  type_time: "now" | "schedule" | "custom";
  days_week?: string | null;
  start_datetime?: string;
  end_datetime?: string;
  products?: WebsiteSystemProduct[];
};

export type UpdateWebsiteSystemResponse = BaseResponse2<{}>;

export const UpdateWebsiteSystem = async (
  request: UpdateWebsiteSystemRequest
): Promise<UpdateWebsiteSystemResponse> => {
  const { token, code, ...data } = request;
  const requestConfig: AxiosRequestConfig = {
    url: `v1/feature-toggle/website-system/${code}`,
    method: "PUT",
    headers: { token },
    data,
  };
  return fetchWithJSON<UpdateWebsiteSystemResponse>(pigspinBackofficeApiInstance, requestConfig);
};

// GET .../website-system/{code}/audit - newest first, no `total` in the
// response (see useWebsiteSystemAudit's useInfiniteQuery for the load-more
// convention that follows from that). `before`/`after` hold only the fields
// that actually changed for that row's write.
export type WebsiteSystemAuditAction = "create" | "edit" | "on" | "off" | "delete" | "auto_off";

export type WebsiteSystemAuditItem = {
  created_at: string;
  actor_name: string | null;
  action: WebsiteSystemAuditAction;
  product: WebsiteSystemProduct | "ALL";
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
};

export type GetWebsiteSystemAuditRequest = BaseRequest & {
  code: string;
  limit?: number;
  offset?: number;
};

export type GetWebsiteSystemAuditResponse = BaseResponse2<{
  audit_list: WebsiteSystemAuditItem[];
}>;

export const GetWebsiteSystemAudit = async (
  request: GetWebsiteSystemAuditRequest
): Promise<GetWebsiteSystemAuditResponse> => {
  const { token, code, ...params } = request;
  const requestConfig: AxiosRequestConfig = {
    url: `v1/feature-toggle/website-system/${code}/audit`,
    method: "GET",
    headers: { token },
    params,
  };
  return fetchWithJSON<GetWebsiteSystemAuditResponse>(pigspinBackofficeApiInstance, requestConfig);
};
